import React, { useEffect, useRef, useState } from 'react';
import { Building2, Users, TrendingDown, Star } from 'lucide-react';
import { statsData } from '../../data/mock';

const iconMap = {
  Building2,
  Users,
  TrendingDown,
  Star
};

// Animated counter hook
const useCountUp = (end, duration = 2000, start = 0, decimals = 0) => {
  const [count, setCount] = useState(start);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const value = progress * (end - start) + start;
      setCount(decimals > 0 ? parseFloat(value.toFixed(decimals)) : Math.floor(value));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration, start, decimals]);

  return { count, ref };
};

const StatItem = ({ value, suffix, label, icon, delay }) => {
  const decimals = value % 1 !== 0 ? 1 : 0;
  const { count, ref } = useCountUp(value, 2000, 0, decimals);
  const Icon = iconMap[icon];

  return (
    <div 
      ref={ref}
      className="text-center p-6 md:p-8"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div 
        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
        style={{ background: 'rgba(211, 255, 98, 0.2)' }}
      >
        <Icon size={32} style={{ color: 'var(--brand-primary)' }} />
      </div>
      <div className="display-medium mb-2" style={{ color: 'white' }}>
        {count}{suffix}
      </div>
      <div className="body-small" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
        {label}
      </div>
    </div>
  );
};

const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-16 md:py-20"
      style={{ background: 'var(--brand-dark)' }}
    >
      <div className="container">
        <div 
          className={`grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 ${
            isVisible ? 'animate-fadeInUp' : 'opacity-0'
          }`}
        >
          {statsData.stats.map((stat, index) => (
            <StatItem
              key={index}
              value={stat.value}
              suffix={stat.suffix}
              label={stat.label}
              icon={stat.icon}
              delay={index * 100}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
